<template>
  <div class="monthly-report">
    <div class="container-fluid py-4">
      <h2 class="h4 fw-medium mb-4">{{ $t('__monthlyReport.title') }}</h2>

      <b-row>
        <b-col lg="4" class="mb-4">
          <b-card>
            <form @submit.prevent="searchResidents">
              <label for="mr-resident-keyword" class="form-label">{{ $t('__residentNameLabel') }}</label>
              <div class="input-group">
                <input
                  id="mr-resident-keyword"
                  v-model.trim="keyword"
                  type="text"
                  class="form-control"
                  :placeholder="$t('__searchResidentPlaceholder')"
                  maxlength="50"
                />
                <button type="submit" class="btn btn-primary">
                  <i class="bi bi-search"></i>
                  {{ $t('__search') }}
                </button>
              </div>
            </form>

            <b-list-group v-if="residents.length > 0" class="mt-3 resident-list">
              <b-list-group-item
                v-for="resident in residents"
                :key="resident.id"
                button
                :active="isSelectedResident(resident)"
                @click="selectResident(resident)"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <span>{{ resident.name }}</span>
                  <small :class="isSelectedResident(resident) ? '' : 'text-muted'">{{ resident.agency_name }}</small>
                </div>
              </b-list-group-item>
            </b-list-group>
            <b-alert v-else-if="hasSearched" show variant="warning" class="mt-3 mb-0">
              {{ $t('__noMatchingResident') }}
            </b-alert>
          </b-card>
        </b-col>

        <b-col lg="8">
          <b-card v-if="!resident" class="mb-4">
            <div v-if="isPendingLoading" class="text-center text-muted py-3">
              <b-spinner small class="me-2"></b-spinner>
              {{ $t('__monthlyReport.pendingLoading') }}
            </div>
            <b-alert v-else-if="pendingErrorMessage" show variant="danger" class="mb-0 d-flex align-items-center justify-content-between">
              <span>{{ pendingErrorMessage }}</span>
              <button type="button" class="btn btn-sm btn-outline-danger" @click="loadPendingResidents">
                {{ $t('__monthlyReport.pendingRetry') }}
              </button>
            </b-alert>
            <template v-else>
              <h6 class="mb-3">{{ pendingTitle }}</h6>
              <b-table
                :items="pendingResidents"
                :fields="pendingFields"
                primary-key="resident_id"
                small
                hover
                responsive
                show-empty
                :empty-text="$t('__monthlyReport.pendingEmpty')"
                class="mb-0 pending-table"
                @row-clicked="selectPendingResident"
              >
                <template #cell(effective_mode)="data">
                  {{ pendingModeText(data.item.effective_mode) }}
                </template>
                <template #cell(comment_status)="data">
                  <span class="badge" :class="'bg-' + commentStatusVariant(data.item.comment_status)">
                    {{ commentStatusText(data.item.comment_status) }}
                  </span>
                </template>
              </b-table>
            </template>
          </b-card>

          <template v-else>
            <b-card class="mb-4">
              <div class="d-flex flex-wrap align-items-end justify-content-between">
                <div class="mb-2">
                  <button type="button" class="btn btn-sm btn-outline-secondary mb-2" @click="backToPendingList">
                    <i class="bi bi-arrow-left"></i>
                    {{ $t('__monthlyReport.backToPending') }}
                  </button>
                  <div class="h5 mb-1">{{ resident.name }}</div>
                  <div class="text-muted small">{{ resident.agency_name }}</div>
                </div>
                <div class="mb-2 d-flex flex-wrap align-items-center">
                  <label for="mr-report-mode" class="me-2 mb-0">{{ $t('__monthlyReport.reportMode') }}</label>
                  <select
                    id="mr-report-mode"
                    v-model="reportMode"
                    :disabled="!canUpdate || isModeSaving"
                    class="form-select w-auto"
                    @change="changeReportMode(reportMode)"
                  >
                    <option v-for="option in reportModeOptions" :key="option.value" :value="option.value">
                      {{ option.text }}
                    </option>
                  </select>
                  <span v-if="autoModeHint" class="badge bg-info ms-2">{{ autoModeHint }}</span>
                </div>
              </div>
            </b-card>

            <b-card class="mb-4" :title="$t('__monthlyReport.monthList')" title-tag="h6">
              <b-table
                :items="months"
                :fields="monthFields"
                :tbody-tr-class="monthRowClass"
                small
                hover
                responsive
                show-empty
                :empty-text="$t('__monthlyReport.noMonths')"
                class="mb-0 month-table"
                @row-clicked="item => selectMonth(item.month)"
              >
                <template #cell(valid_days)="data">
                  {{ data.item.has_report ? data.item.valid_days : '-' }}
                </template>
                <template #cell(effective_mode)="data">
                  {{ modeText(data.item.effective_mode) }}
                </template>
                <template #cell(comment_status)="data">
                  <span class="badge" :class="'bg-' + commentStatusVariant(data.item.comment_status)">
                    {{ commentStatusText(data.item.comment_status) }}
                  </span>
                </template>
              </b-table>
            </b-card>

            <template v-if="detail">
              <b-card class="mb-4" :title="$t('__monthlyReport.monthTitle', { month: detail.month })" title-tag="h6">
                <b-alert v-if="!detail.has_report" show variant="secondary" class="mb-0">
                  {{ $t('__monthlyReport.noReportData') }}
                </b-alert>
                <template v-else>
                  <div class="mb-3">
                    {{ $t('__monthlyReport.validDays') }}：<strong>{{ detail.valid_days }}</strong>
                    <span class="ms-3 text-muted">{{ $t('__monthlyReport.effectiveMode') }}：{{ modeText(detail.effective_mode) }}</span>
                  </div>

                  <div v-if="detail.scores" class="d-flex flex-wrap mb-3">
                    <div v-for="indicator in indicators" :key="indicator.key" class="indicator me-3 mb-2">
                      <span class="me-1">{{ $t(indicator.labelKey) }}</span>
                      <span class="badge" :class="'bg-' + indicator.variant">{{ indicator.text }}</span>
                    </div>
                  </div>

                  <b-row v-if="detail.averages">
                    <b-col v-for="item in averageItems" :key="item.key" cols="6" md="4" class="mb-3">
                      <div class="text-muted small">{{ $t(item.labelKey) }}</div>
                      <div class="fw-bold">
                        {{ item.value }}
                        <small v-if="item.unitKey && item.value !== '-'" class="fw-normal text-muted">{{ $t(item.unitKey) }}</small>
                      </div>
                    </b-col>
                  </b-row>
                </template>
              </b-card>

              <b-card class="mb-4" :title="$t('__monthlyReport.timelineTitle')" title-tag="h6">
                <div v-if="isTimelineLoading" class="text-center text-muted py-3">
                  <b-spinner small class="me-2"></b-spinner>
                  {{ $t('__monthlyReport.timelineLoading') }}
                </div>
                <b-alert v-else-if="timelineErrorMessage" show variant="danger" class="mb-0 d-flex align-items-center justify-content-between">
                  <span>{{ timelineErrorMessage }}</span>
                  <button type="button" class="btn btn-sm btn-outline-danger" @click="loadTimeline">
                    {{ $t('__monthlyReport.timelineRetry') }}
                  </button>
                </b-alert>
                <b-alert v-else-if="!hasTimeline" show variant="secondary" class="mb-0">
                  {{ $t('__monthlyReport.timelineEmpty') }}
                </b-alert>
                <bed-timeline-30-days v-else :windows="timelineWindows"></bed-timeline-30-days>
              </b-card>

              <comment-form
                :key="`${resident.id}-${detail.month}-${detail.comment_mode}`"
                :resident-id="resident.id"
                :month="detail.month"
                :comment-mode="detail.comment_mode"
                :comments="detail.comments"
                :can-update="canUpdate"
                @dirty-change="value => (isCommentDirty = value)"
                @changed="onCommentChanged"
              ></comment-form>
            </template>
          </template>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import { monthlyReport, isSuccessResponse, responseErrorMessage } from '@/http/api/monthlyReport'
import CommentForm from '@/components/monthlyReport/CommentForm.vue'
import BedTimeline30Days from '@/components/monthlyReport/BedTimeline30Days.vue'
import {
  MODE_ACTIVE,
  MODE_BED,
  MODE_AUTO,
  COMMENT_STATUS_VARIANTS,
  scoreMeta,
  indicatorKeys,
  formatNumber,
  formatClockHour,
  hasTimelineData
} from '@/components/monthlyReport/format'

const INDICATOR_LABEL_KEYS = {
  respiration: '__monthlyReport.indicatorRespiration',
  routine: '__monthlyReport.indicatorRoutine',
  care: '__monthlyReport.indicatorCare',
  rest: '__monthlyReport.indicatorRest'
}

export default {
  name: 'MonthlyReport',
  components: { CommentForm, BedTimeline30Days },
  data () {
    return {
      keyword: '',
      residents: [],
      hasSearched: false,
      resident: null,
      reportMode: null,
      isModeSaving: false,
      months: [],
      selectedMonth: null,
      detail: null,
      isCommentDirty: false,
      pendingMonth: '',
      pendingResidents: [],
      isPendingLoading: false,
      pendingErrorMessage: '',
      timelineWindows: [],
      isTimelineLoading: false,
      timelineErrorMessage: ''
    }
  },
  computed: {
    ...mapState(['token']),
    ...mapGetters(['hasPermission']),
    canUpdate () {
      return this.hasPermission('monthly-report.update')
    },
    timelineKey () {
      return this.resident && this.selectedMonth ? `${this.resident.id}/${this.selectedMonth}` : null
    },
    hasTimeline () {
      return hasTimelineData(this.timelineWindows)
    },
    reportModeOptions () {
      return [
        { value: MODE_AUTO, text: this.$t('__monthlyReport.modeAuto') },
        { value: MODE_ACTIVE, text: this.$t('__monthlyReport.modeActive') },
        { value: MODE_BED, text: this.$t('__monthlyReport.modeBed') }
      ]
    },
    autoModeHint () {
      if (this.reportMode !== MODE_AUTO || !this.detail || !this.detail.auto_mode) return ''
      return this.$t('__monthlyReport.autoModeOfMonth', { mode: this.modeText(this.detail.auto_mode) })
    },
    monthFields () {
      return [
        { key: 'month', label: this.$t('__monthlyReport.month') },
        { key: 'valid_days', label: this.$t('__monthlyReport.validDays'), class: 'text-center' },
        { key: 'effective_mode', label: this.$t('__monthlyReport.effectiveMode') },
        { key: 'comment_status', label: this.$t('__monthlyReport.commentStatus'), class: 'text-center' }
      ]
    },
    pendingTitle () {
      const [year, month] = this.pendingMonth.split('-')
      return this.$t('__monthlyReport.pendingTitle', {
        year,
        month: Number(month),
        count: this.pendingResidents.length
      })
    },
    pendingFields () {
      return [
        { key: 'name', label: this.$t('__monthlyReport.pendingColResident') },
        { key: 'agency_name', label: this.$t('__monthlyReport.pendingColAgency') },
        { key: 'effective_mode', label: this.$t('__monthlyReport.pendingColMode') },
        { key: 'comment_status', label: this.$t('__monthlyReport.pendingColStatus'), class: 'text-center' }
      ]
    },
    indicators () {
      const scores = (this.detail && this.detail.scores) || {}
      return indicatorKeys(this.detail && this.detail.effective_mode).map(key => {
        const meta = scoreMeta(scores[key])
        return {
          key,
          labelKey: INDICATOR_LABEL_KEYS[key],
          variant: meta ? meta.variant : 'secondary',
          text: meta ? this.$t(meta.labelKey) : '-'
        }
      })
    },
    averageItems () {
      const avg = (this.detail && this.detail.averages) || {}
      return [
        { key: 'avg_respiration', labelKey: '__monthlyReport.avgRespiration', unitKey: '__monthlyReport.unitPerMinute', value: formatNumber(avg.avg_respiration) },
        { key: 'avg_night_sleep', labelKey: '__monthlyReport.avgNightSleep', unitKey: '__monthlyReport.unitHour', value: formatNumber(avg.avg_night_sleep) },
        { key: 'avg_asleep_start_hour', labelKey: '__monthlyReport.avgAsleepStart', unitKey: null, value: formatClockHour(avg.avg_asleep_start_hour) },
        { key: 'avg_asleep_leave', labelKey: '__monthlyReport.avgAsleepLeave', unitKey: '__monthlyReport.unitTimes', value: formatNumber(avg.avg_asleep_leave) },
        { key: 'avg_leave_bed_total', labelKey: '__monthlyReport.avgLeaveBedTotal', unitKey: '__monthlyReport.unitHour', value: formatNumber(avg.avg_leave_bed_total) },
        { key: 'sleep_efficiency_percent', labelKey: '__monthlyReport.sleepEfficiency', unitKey: '__monthlyReport.unitPercent', value: formatNumber(avg.sleep_efficiency_percent) },
        { key: 'avg_night_turn_interval', labelKey: '__monthlyReport.avgNightTurnInterval', unitKey: '__monthlyReport.unitMinute', value: formatNumber(avg.avg_night_turn_interval) },
        { key: 'avg_day_turn_interval', labelKey: '__monthlyReport.avgDayTurnInterval', unitKey: '__monthlyReport.unitMinute', value: formatNumber(avg.avg_day_turn_interval) }
      ]
    }
  },
  watch: {
    // 回到未選住民時重抓，剛發布的住民才會從待填清單消失
    resident (value, oldValue) {
      if (!value && oldValue) this.loadPendingResidents()
    },
    // 只跟住民與月份連動，切換月報類型重抓 detail 時不必重抓作息
    timelineKey (value) {
      this.timelineWindows = []
      this.timelineErrorMessage = ''
      this.isTimelineLoading = false
      if (value) this.loadTimeline()
    }
  },
  created () {
    this.loadPendingResidents()
  },
  methods: {
    ...mapMutations(['Loading', 'Loaded', 'AlertsInfo', 'Alerted']),

    notifyError (info) {
      this.AlertsInfo({ state: 'error', title: this.$t('__error'), info })
      this.Alerted()
    },

    // 評語有未儲存變更時，切換畫面前先確認
    confirmDiscard () {
      return !this.isCommentDirty || confirm(this.$t('__monthlyReport.discardConfirm'))
    },

    isSelectedResident (resident) {
      return Boolean(this.resident && this.resident.id === resident.id)
    },

    modeText (mode) {
      if (mode === MODE_ACTIVE) return this.$t('__monthlyReport.modeActive')
      if (mode === MODE_BED) return this.$t('__monthlyReport.modeBed')
      return '-'
    },

    commentStatusVariant (status) {
      return COMMENT_STATUS_VARIANTS[status] || 'secondary'
    },

    commentStatusText (status) {
      if (status === 'published') return this.$t('__monthlyReport.statusPublished')
      if (status === 'draft') return this.$t('__monthlyReport.statusDraft')
      return this.$t('__monthlyReport.statusNone')
    },

    pendingModeText (mode) {
      return mode ? this.modeText(mode) : this.$t('__monthlyReport.modeUndetermined')
    },

    monthRowClass (item) {
      return item && item.month === this.selectedMonth ? 'table-active' : ''
    },

    async searchResidents () {
      this.Loading()
      try {
        const response = await monthlyReport.searchResidents(this.keyword, this.token)
        if (!isSuccessResponse(response)) {
          this.notifyError(responseErrorMessage(response, this.$t('__searchFailed')))
          return
        }
        this.residents = response.data.data || []
        this.hasSearched = true
      } catch (error) {
        console.error('搜尋月報住民失敗:', error)
        this.notifyError(this.$t('__searchFailed'))
      } finally {
        this.Loaded()
      }
    },

    async loadPendingResidents () {
      this.isPendingLoading = true
      this.pendingErrorMessage = ''
      try {
        const response = await monthlyReport.getPendingResidents(this.token)
        if (!isSuccessResponse(response)) {
          this.pendingErrorMessage = responseErrorMessage(response, this.$t('__monthlyReport.pendingLoadFailed'))
          return
        }
        this.pendingMonth = response.data.data.month
        this.pendingResidents = response.data.data.residents || []
      } catch (error) {
        console.error('載入待填月報清單失敗:', error)
        this.pendingErrorMessage = this.$t('__monthlyReport.pendingLoadFailed')
      } finally {
        this.isPendingLoading = false
      }
    },

    backToPendingList () {
      if (!this.confirmDiscard()) return
      this.detail = null
      this.selectedMonth = null
      this.months = []
      this.reportMode = null
      this.isCommentDirty = false
      this.resident = null
    },

    selectPendingResident (item) {
      this.selectResident({ id: item.resident_id }, this.pendingMonth)
    },

    // 指定 month 且該住民有這個月份時直接開該月，否則開最新月份
    async selectResident (resident, month) {
      if (this.isSelectedResident(resident) || !this.confirmDiscard()) return
      this.detail = null
      this.selectedMonth = null
      const isLoaded = await this.loadMonths(resident.id)
      if (!isLoaded || this.months.length === 0) return
      const hasMonth = this.months.some(item => item.month === month)
      await this.loadDetail(hasMonth ? month : this.months[0].month)
    },

    async loadMonths (residentId) {
      this.Loading()
      try {
        const response = await monthlyReport.getResidentMonths(residentId, this.token)
        if (!isSuccessResponse(response)) {
          this.notifyError(responseErrorMessage(response, this.$t('__monthlyReport.loadFailed')))
          return false
        }
        this.resident = response.data.data.resident
        this.reportMode = this.resident.monthly_report_mode
        this.months = response.data.data.months || []
        return true
      } catch (error) {
        console.error('載入月報月份失敗:', error)
        this.notifyError(this.$t('__monthlyReport.loadFailed'))
        return false
      } finally {
        this.Loaded()
      }
    },

    selectMonth (month) {
      if (month === this.selectedMonth || !this.confirmDiscard()) return
      this.loadDetail(month)
    },

    async loadDetail (month) {
      this.Loading()
      try {
        const response = await monthlyReport.getMonthDetail(this.resident.id, month, this.token)
        if (!isSuccessResponse(response)) {
          this.notifyError(responseErrorMessage(response, this.$t('__monthlyReport.loadFailed')))
          return
        }
        this.selectedMonth = month
        this.detail = response.data.data
      } catch (error) {
        console.error('載入單月月報失敗:', error)
        this.notifyError(this.$t('__monthlyReport.loadFailed'))
      } finally {
        this.Loaded()
      }
    },

    // 快速切月份時舊請求可能較晚回來，回應時 key 已變就丟棄
    async loadTimeline () {
      const requestKey = this.timelineKey
      if (!requestKey) return
      this.isTimelineLoading = true
      this.timelineErrorMessage = ''
      try {
        const response = await monthlyReport.getTimeline(this.resident.id, this.selectedMonth, this.token)
        if (requestKey !== this.timelineKey) return
        if (!isSuccessResponse(response)) {
          this.timelineErrorMessage = responseErrorMessage(response, this.$t('__monthlyReport.timelineLoadFailed'))
          return
        }
        this.timelineWindows = (response.data.data && response.data.data.windows) || []
      } catch (error) {
        if (requestKey !== this.timelineKey) return
        console.error('載入 30 日作息失敗:', error)
        this.timelineErrorMessage = this.$t('__monthlyReport.timelineLoadFailed')
      } finally {
        if (requestKey === this.timelineKey) this.isTimelineLoading = false
      }
    },

    async changeReportMode (mode) {
      const previousMode = this.resident.monthly_report_mode
      if (!this.confirmDiscard()) {
        this.reportMode = previousMode
        return
      }
      this.isModeSaving = true
      this.Loading()
      try {
        const response = await monthlyReport.updateMode(this.resident.id, mode, this.token)
        if (!isSuccessResponse(response)) {
          this.reportMode = previousMode
          this.notifyError(responseErrorMessage(response, this.$t('__monthlyReport.modeSaveFailed')))
          return
        }
        this.AlertsInfo({ state: 'success', title: this.$t('__success'), info: this.$t('__monthlyReport.modeSaveSuccess') })
        this.Alerted()
      } catch (error) {
        console.error('更新月報類型失敗:', error)
        this.reportMode = previousMode
        this.notifyError(this.$t('__monthlyReport.modeSaveFailed'))
        return
      } finally {
        this.isModeSaving = false
        this.Loaded()
      }
      // 類型影響每個月份的生效版本，需重新載入
      await this.loadMonths(this.resident.id)
      if (this.selectedMonth) await this.loadDetail(this.selectedMonth)
    },

    // 評語狀態以後端計算為準，存檔或發布後重抓月份清單
    onCommentChanged ({ mode, comment }) {
      this.detail.comments = Object.assign({}, this.detail.comments, { [mode]: comment })
      this.loadMonths(this.resident.id)
    }
  }
}
</script>

<style scoped>
.resident-list {
  max-height: 60vh;
  overflow-y: auto;
}
.month-table ::v-deep tbody tr,
.pending-table ::v-deep tbody tr {
  cursor: pointer;
}
</style>
